import { useState } from 'react';
import type { ReactNode } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { Label } from '@ooh/ui/components/label';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import type { MultiSelectComboboxOption } from '@ooh/ui/components/multi-select-combobox';
import { toast } from '@ooh/ui/components/sonner';
import { adminUsers, permissions, UpdateUserSchema, parseApiError } from '@ooh/contracts';
import type {
  UserAdmin,
  UserAdminListItem,
  UserPermissionOverride,
  PermissionGrouped,
  Permission,
} from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { apiQueryKey, useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { FormSkeleton } from '../../layouts/skeletons';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';
import { invalidateUserQueries } from './user-queries';
import {
  type OverrideState,
  nextOverrideState,
  grantedToOverrideState,
  PermissionOverridesSection,
  DeactivateConfirmDialog,
  RolePickerField,
} from './edit-user-dialog-parts';
import { SetPasswordAction } from './set-password-dialog';

const EDIT_USER_FORM_ID = 'edit-user-form';
const EDIT_USER_DIALOG_MAX_WIDTH = 'max-w-xl';

class PartialSaveError extends Error {
  readonly isPartialSave = true;
  constructor(cause: unknown) {
    super('partial-save');
    this.cause = cause;
  }
}

// Extracted so the Save button's disabled expression doesn't add another
// branch to EditUserDialog's own cyclomatic complexity.
function isSaveDisabled(isSubmitting: boolean, isPending: boolean, isValid: boolean): boolean {
  return isSubmitting || isPending || !isValid;
}

function groupSections(grouped: PermissionGrouped): [string, Permission[]][] {
  return Object.entries(grouped) as [string, Permission[]][];
}

const PermissionOverrideSchema = z.object({
  code: z.string(),
  resource: z.string(),
  name: z.string(),
  state: z.enum(['inherit', 'grant', 'revoke']),
});
const EditUserFormSchema = UpdateUserSchema.extend({
  overrides: z.array(PermissionOverrideSchema),
});
type EditUserForm = z.infer<typeof EditUserFormSchema>;

function buildDefaultValues(
  detail: UserAdmin,
  overridesData: UserPermissionOverride[],
  allPermsGrouped: PermissionGrouped,
): EditUserForm {
  const overrideMap = new Map(
    overridesData.map((o) => [o.permissionCode.replace(/^action:/, ''), o.granted]),
  );
  const flatPerms: Permission[] = Object.values(allPermsGrouped).flat() as Permission[];
  return {
    name: detail.name,
    roleIds: detail.roles.map((r) => r.id),
    isActive: detail.isActive,
    overrides: flatPerms.map((p) => ({
      code: p.code,
      resource: p.resource,
      name: p.name,
      state: grantedToOverrideState(overrideMap.get(p.code)),
    })),
  };
}

interface EditUserDialogProps {
  user: UserAdminListItem;
  currentUserId?: number;
  onClose: () => void;
  onSuccess: () => void;
}

function EditUserForm({ user, currentUserId, onClose, onSuccess }: EditUserDialogProps) {
  const { t } = useTranslation('admin');
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  const { data: userDetail } = useApiSuspenseQuery(adminUsers.getUser, { id: user.id });
  const { data: overridesData } = useApiSuspenseQuery(adminUsers.listUserOverrides, {
    id: user.id,
  });
  const { data: roles } = useApiSuspenseQuery(permissions.getRoles, {});
  const roleOptions: MultiSelectComboboxOption[] = roles.map((r) => ({
    value: String(r.id),
    label: r.name,
  }));
  const { data: allPermsGrouped } = useApiSuspenseQuery(permissions.getAll, {});

  const sections = groupSections(allPermsGrouped);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { isSubmitting, isValid },
  } = useForm<EditUserForm>({
    resolver: zodResolver(EditUserFormSchema),
    mode: 'onChange',
    defaultValues: buildDefaultValues(userDetail, overridesData, allPermsGrouped),
  });

  const overrides = watch('overrides');

  const updateMutation = useMutation({
    mutationFn: async (data: EditUserForm) => {
      const patch = { name: data.name, roleIds: data.roleIds, isActive: data.isActive };
      await api.call(adminUsers.updateUser, { id: user.id, ...patch });
      const overridePayload = data.overrides
        .filter((o) => o.state !== 'inherit')
        .map((o) => ({ permissionCode: o.code, granted: o.state === 'grant' }));
      try {
        await api.call(adminUsers.updateUserOverrides, {
          id: user.id,
          overrides: overridePayload,
        });
      } catch (err) {
        toast.error(t('users.edit.errors.partialSave'));
        // The PATCH succeeded, so only the list and the detail are stale; the overrides were not saved.
        void queryClient.invalidateQueries({ queryKey: apiQueryKey(adminUsers.listUsers) });
        void queryClient.invalidateQueries({
          queryKey: apiQueryKey(adminUsers.getUser, { id: user.id }),
          exact: true,
        });
        throw new PartialSaveError(err);
      }
    },
    onSuccess: () => {
      void invalidateUserQueries(queryClient);
      onSuccess();
    },
    onError: (err) => {
      if (err instanceof PartialSaveError) return;
      toast.error(apiErrorToUserMessage(parseApiError(err), 'mutation'));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => api.call(adminUsers.updateUser, { id: user.id, isActive: false }),
    onSuccess: () => {
      void invalidateUserQueries(queryClient);
      onSuccess();
    },
    onError: (err) => {
      toast.error(apiErrorToUserMessage(parseApiError(err), 'mutation'));
    },
  });

  const overrideStateLabel: Record<OverrideState, string> = {
    inherit: t('users.edit.overrides.inherit'),
    grant: t('users.edit.overrides.grant'),
    revoke: t('users.edit.overrides.revoke'),
  };

  const showDeactivate = user.isActive && user.id !== currentUserId;

  return (
    <>
      <EditUserDialogFrame
        user={user}
        onClose={onClose}
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <SetPasswordAction userId={user.id} />
              {showDeactivate ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeactivateDialog(true)}
                  disabled={deactivateMutation.isPending}
                >
                  {t('users.actions.deactivate')}
                </Button>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('users.actions.cancel')}
              </Button>
              <Button
                type="submit"
                form={EDIT_USER_FORM_ID}
                disabled={isSaveDisabled(isSubmitting, updateMutation.isPending, isValid)}
              >
                {isSubmitting || updateMutation.isPending
                  ? t('users.actions.saving')
                  : t('users.actions.save')}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id={EDIT_USER_FORM_ID}
          onSubmit={handleSubmit((data) => void updateMutation.mutateAsync(data))}
          className="space-y-5"
        >
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-email">{t('users.edit.fields.email')}</Label>
              <Input
                id="edit-email"
                value={user.email}
                readOnly
                className="bg-muted/40 text-muted-foreground cursor-not-allowed"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">{t('users.edit.fields.name')}</Label>
              <Input id="edit-name" {...register('name')} />
            </div>
            <Controller
              name="roleIds"
              control={control}
              render={({ field }) => (
                <RolePickerField
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={roleOptions}
                  label={t('users.edit.fields.role')}
                  placeholder={t('users.edit.fields.rolePlaceholder')}
                  searchPlaceholder={t('users.edit.fields.roleSearchPlaceholder')}
                  emptyText={t('users.edit.fields.roleNoResults')}
                />
              )}
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="edit-active"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <Label htmlFor="edit-active" className="cursor-pointer">
                    {t('users.edit.fields.active')}
                  </Label>
                </div>
              )}
            />
          </div>
          <PermissionOverridesSection
            sections={sections}
            overrides={overrides}
            overrideStateLabel={overrideStateLabel}
            onToggle={(code, current) => {
              setValue(
                'overrides',
                current.map((o) =>
                  o.code === code ? { ...o, state: nextOverrideState(o.state) } : o,
                ),
              );
            }}
            title={t('users.edit.overrides.title')}
            description={t('users.edit.overrides.description')}
            sectionLabel={(resource) =>
              t(`users.edit.overrides.sections.${resource}`, { defaultValue: resource })
            }
          />
        </form>
      </EditUserDialogFrame>
      {showDeactivateDialog ? (
        <DeactivateConfirmDialog
          isPending={deactivateMutation.isPending}
          title={t('users.edit.deactivateDialog.title')}
          description={t('users.edit.deactivateDialog.description')}
          cancelLabel={t('users.edit.deactivateDialog.cancel')}
          confirmLabel={t('users.edit.deactivateDialog.confirm')}
          onCancel={() => setShowDeactivateDialog(false)}
          onConfirm={() => {
            setShowDeactivateDialog(false);
            void deactivateMutation.mutateAsync();
          }}
        />
      ) : null}
    </>
  );
}

function EditUserDialogFrame({
  user,
  onClose,
  footer,
  children,
}: {
  user: UserAdminListItem;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation('admin');

  return (
    <ModalDialog
      onClose={onClose}
      closeLabel={t('users.edit.close')}
      maxWidth={EDIT_USER_DIALOG_MAX_WIDTH}
      header={
        <>
          <h2 className="text-[16px] font-semibold">{t('users.edit.title')}</h2>
          <p className="text-[12px] text-muted-foreground">{user.email}</p>
        </>
      }
      footer={footer}
    >
      {children}
    </ModalDialog>
  );
}

export function EditUserDialog({ user, currentUserId, onClose, onSuccess }: EditUserDialogProps) {
  return (
    <QueryBoundary
      fallback={<FormSkeleton />}
      renderFrame={(content) => (
        <EditUserDialogFrame user={user} onClose={onClose}>
          {content}
        </EditUserDialogFrame>
      )}
    >
      <EditUserForm
        user={user}
        currentUserId={currentUserId}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </QueryBoundary>
  );
}
